import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import AudioControls from './AudioControls';
import StudioIcon from './StudioIcon';
import AIRewrite from './AIRewrite';
import VoiceInput from './VoiceInput';

export default function PromptCard({
  mediaType,
  subject,
  onChangeSubject,
  onChangeMediaType,
  onGenerate,
  isGenerating,
  lastError,
  onOpenSettings,
  showPreviewButton,
  onOpenPreview,
  previewDisabled,
  settings,
  audioModels,
  onChangeAudioSetting,
  audioFile,
  onAudioFileChange,
  modelLabel,
  brandName,
  brandProfile,
  requestPanel,
  generationBlocked,
  formatLabel,
}) {
  const { t, locale } = useI18n();
  const ref = useRef(null);
  const [isDictating, setIsDictating] = useState(false);
  const [brandPreview, setBrandPreview] = useState(null);
  const [brandUndo, setBrandUndo] = useState(null);
  const brandText = [brandProfile?.name && `Brand: ${brandProfile.name}`, brandProfile?.description && `About: ${brandProfile.description}`, brandProfile?.tone && `Tone: ${brandProfile.tone}`, brandProfile?.palette?.length && `Colors: ${brandProfile.palette.join(', ')}`].filter(Boolean).join('\n');
  const ar = locale === 'ar';
  const inputBusy = isGenerating || isDictating;
  const audioOperation = settings?.audioOperation || 'speech';
  const audioCopy = {
    speech: { label: 'Script', placeholder: t('promptCard.placeholderAudio') },
    music: { label: 'Track description', placeholder: 'Cinematic electronic track with a confident build and clean ending' },
    sfx: { label: 'Sound description', placeholder: 'A short futuristic restaurant notification chime' },
  }[audioOperation] || { label: t('promptCard.fieldLabelAudio'), placeholder: t('promptCard.placeholderAudio') };
  const typeCards = [
    {
      id: 'image',
      icon: '✦',
      title: 'Image',
      body: 'Posts, ads, product scenes',
    },
    {
      id: 'video',
      icon: '▶',
      title: 'Video',
      body: 'Reels, motion concepts',
    },
    {
      id: 'audio',
      icon: '♪',
      title: 'Audio',
      body: 'Speech, music, SFX and transcripts',
    },
    {
      id: 'text',
      icon: 'Aa',
      title: 'Text',
      body: 'Copy and captions',
    },
  ];
  const starters = {
    image: [
      'A premium product photograph on a warm stone pedestal, soft morning light, subtle shadows, minimal composition, space for a headline',
      'An eye-catching social media campaign for our brand, bold composition, thoughtful use of brand colors, clean background, no text',
      'An editorial still life with sculptural objects, natural textures, warm directional light, refined color palette, magazine photography',
    ],
    video: [
      'A slow cinematic product reveal, camera orbiting a stone pedestal, warm studio lighting, elegant motion, clean background',
      'A short brand story showing the care behind a beautifully crafted product, close-up details, warm natural light',
      'A seamless cinematic loop of flowing fabric in brand colors, soft light, gentle movement, minimal composition',
    ],
    text: [
      'Write three engaging Instagram captions for our brand. Keep them warm and concise, with a clear call to action.',
      'Write a product launch announcement for our brand. Use a confident, friendly tone. Include a headline and a short call to action.',
      'Write a short About Us story for our brand, focusing on our purpose, the people we help, and what makes us different.',
    ],
    audio: [
      'Welcome. We are so glad you are here. Discover something made just for you.',
      'Something new is on the way. A fresh idea, a thoughtful detail, a whole new experience. Stay tuned.',
      'Every great brand starts with a simple idea. Ours is to make everyday moments a little more remarkable.',
    ],
  };
  const starterKeys = ['One', 'Two', 'Three'];
  const arabicStarters = {
    image: ['صورة احترافية لمنتج على قاعدة حجرية بإضاءة صباحية دافئة وظلال ناعمة وتكوين بسيط مع مساحة لعنوان', 'حملة بصرية لعلامتنا على وسائل التواصل، تكوين جريء وألوان متناسقة مع الهوية وخلفية نظيفة بدون نصوص', 'تكوين تحريري من عناصر منحوتة وخامات طبيعية وإضاءة جانبية دافئة، بأسلوب تصوير المجلات'],
    video: ['كشف سينمائي بطيء لمنتج على قاعدة حجرية، حركة كاميرا دائرية وإضاءة دافئة وخلفية بسيطة', 'قصة قصيرة عن العناية بتفاصيل منتج مصنوع بإتقان، لقطات قريبة وإضاءة طبيعية دافئة', 'حلقة سينمائية متصلة من قماش متدفق بألوان العلامة، إضاءة ناعمة وحركة هادئة'],
    text: ['اكتب ثلاثة منشورات إنستغرام لعلامتنا بلغة عربية ودودة وموجزة، مع دعوة واضحة للتفاعل', 'اكتب إعلان إطلاق منتج لعلامتنا بالعربية، بنبرة واثقة وودودة، مع عنوان ودعوة قصيرة لاتخاذ إجراء', 'اكتب قصة قصيرة عن علامتنا بالعربية، تشرح هدفنا والأشخاص الذين نخدمهم وما يميزنا'],
    audio: ['أهلاً بك. سعداء بوجودك معنا. اكتشف تجربة صُمّمت خصيصاً لك.', 'شيء جديد في الطريق. فكرة مبتكرة وتفاصيل مدروسة وتجربة مختلفة. ترقّبوا المزيد.', 'كل علامة عظيمة تبدأ بفكرة بسيطة. فكرتنا أن نجعل لحظاتك اليومية أكثر تميزاً.'],
  };

  useEffect(() => {
    if (!lastError) return;
    if (!/Subject/i.test(String(lastError))) return;
    ref.current?.focus?.();
  }, [lastError]);

  return (
    <div className="PromptCard">
      <div className="CreateTypeGrid" aria-label="Choose what to create">
        {typeCards.map((card) => {
          const active = mediaType === card.id;
          return (
            <button
              key={card.id}
              type="button"
              className={`CreateTypeCard ${active ? 'CreateTypeCard--active' : ''}`}
              onClick={() => onChangeMediaType?.(card.id)}
              disabled={isGenerating}
              aria-pressed={active}
            >
              <span className="CreateTypeIcon"><StudioIcon name={card.id} size={19} /></span>
              <span className="CreateTypeText">
                <span className="CreateTypeTitle">{t(`media.${card.id}`)}</span>
                <span className="CreateTypeBody">{card.body}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="PromptHeader">
        <div>
          <div className="PromptEyebrow">{t('studio.brief')}</div>
          <h2 className="PromptTitle">{t('studio.briefTitle')}</h2>
          <div className="PromptHint">
            {mediaType === 'audio'
              ? t('promptCard.hintAudio')
              : mediaType === 'text'
                ? t('promptCard.hintText')
                : t('promptCard.hintImage')}
          </div>
        </div>
        <div className="PromptActions">
          {showPreviewButton ? (
            <button className="Btn Btn--ghost" type="button" onClick={onOpenPreview} disabled={Boolean(previewDisabled)}>
              {t('common.preview')}
            </button>
          ) : null}
        </div>
      </div>

      {mediaType === 'audio' ? (
        <fieldset className="AudioControlsLock" disabled={isGenerating}>
          <AudioControls settings={settings} models={audioModels} onChange={onChangeAudioSetting} file={audioFile} onFileChange={onAudioFileChange} />
        </fieldset>
      ) : null}

      {mediaType === 'audio' && settings?.audioOperation === 'transcription' ? null : <label className="Field" htmlFor="subject">
        <div className="Field-label">
          {mediaType === 'audio'
            ? audioCopy.label
            : mediaType === 'text'
              ? t('promptCard.fieldLabelText')
              : t('promptCard.fieldLabelImage')}
        </div>
        <textarea
          id="subject"
          ref={ref}
          rows={4}
          placeholder={
            mediaType === 'audio'
              ? audioCopy.placeholder
              : mediaType === 'text'
                ? t('promptCard.placeholderText')
                : t('promptCard.placeholderImage')
          }
          value={subject || ''}
          readOnly={isDictating}
          onChange={(e) => onChangeSubject(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !inputBusy && !generationBlocked && subject?.trim()) {
              e.preventDefault();
              onGenerate();
            }
          }}
        />
      </label>}
      {!(mediaType === 'audio' && audioOperation === 'transcription') ? <VoiceInput key={`${mediaType}:${audioOperation}:${locale}`} value={subject} onChange={onChangeSubject} disabled={isGenerating} onActiveChange={setIsDictating} /> : null}
      {!(mediaType === 'audio' && audioOperation === 'transcription') ? <AIRewrite value={subject} onApply={onChangeSubject} mediaType={mediaType} disabled={inputBusy} /> : null}
      {!(mediaType === 'audio' && audioOperation !== 'speech') ? <>
        <div className="BriefMeta"><span>{t('studio.inspiration')}</span><span>{(subject || '').length} {t('studio.characters')}</span></div>
        <div className="PromptStarters">
          {(starters[mediaType] || starters.image).map((prompt, index) => <button key={index} type="button" disabled={inputBusy} onClick={() => {
            onChangeSubject(locale === 'ar' ? arabicStarters[mediaType]?.[index] || prompt : prompt);
            ref.current?.focus();
          }}>{t(`studio.${mediaType}${starterKeys[index]}`)} <span aria-hidden="true">↗</span></button>)}
        </div>
      </> : null}
      {mediaType !== 'audio' ? <div className="BrandInsertion">
        <p className="Hint">{ar ? 'يُرسل وصفك حرفياً. إعدادات الأسلوب القديمة لا تضيف نصاً تلقائياً.' : 'Your prompt is sent exactly as written. Previous style settings no longer add hidden instructions.'}</p>
        <button type="button" className="Btn Btn--ghost" disabled={inputBusy || !brandText} onClick={() => setBrandPreview(brandText)}>{ar ? 'أضف هوية العلامة' : 'Add brand identity'}</button>
        {brandPreview !== null ? <div className="CardFlat">
          <pre className="Pre">{brandPreview}</pre>
          <button type="button" className="Btn" disabled={inputBusy} onClick={() => { const next = `${subject || ''}${subject ? '\n\n' : ''}${brandPreview}`; setBrandUndo({before: subject || '', after: next}); onChangeSubject(next); setBrandPreview(null); }}>{ar ? 'أدرج داخل الوصف' : 'Insert into prompt'}</button>
          <button type="button" className="Btn Btn--ghost" onClick={() => setBrandPreview(null)}>{ar ? 'إلغاء' : 'Cancel'}</button>
        </div> : null}
        {brandUndo ? <button type="button" className="Btn Btn--ghost" disabled={inputBusy} onClick={() => { if (subject === brandUndo.after || subject === brandUndo.before) onChangeSubject(brandUndo.before); else onChangeSubject(subject.replace(brandUndo.after, brandUndo.before)); setBrandUndo(null); }}>{ar ? 'تراجع عن إدراج الهوية' : 'Undo brand insertion'}</button> : null}
      </div> : null}
      <div className="QuickSettings" aria-label="Quick settings">
        <button type="button" className="QuickSetting" onClick={onOpenSettings}><span>{t('studio.settings')} <StudioIcon name="settings" size={15} /></span><strong>{modelLabel || 'Auto'} · {formatLabel || 'Auto'}</strong></button>
        <button type="button" className="QuickSetting" onClick={onOpenSettings}><span>{t('brand.profile')}</span><strong>{brandName || 'None'}</strong></button>
      </div>
      {requestPanel}
      <div className="GenerateArea">
        <button className={`GenerateButton ${isGenerating ? 'GenerateButton--busy' : ''}`} aria-busy={Boolean(isGenerating)} type="button" onClick={onGenerate} disabled={inputBusy || generationBlocked || (!(mediaType === 'audio' && settings?.audioOperation === 'transcription') && !subject?.trim())}>
          {isGenerating ? <span className="GenerateSpinner" aria-hidden="true" /> : <StudioIcon />} {isGenerating ? t('common.generating') : t('common.generate')} <StudioIcon name={isGenerating ? mediaType : 'arrow'} size={18} />
        </button>
        {isGenerating ? <a className="GenerationJump" href="#studio-canvas">{t('generation.viewProgress')} <span aria-hidden="true">↓</span></a> : <span>{t('studio.shortcut')}</span>}
      </div>
    </div>
  );
}
